import React, { useState } from 'react';
import { PageView } from '../types';
import { MuseForgePlayground } from '../components/MuseForgePlayground';
import { PlatformArchitectureVisualizer } from '../components/PlatformArchitectureVisualizer';
import { 
  ShieldCheck, 
  ArrowRight, 
  Terminal, 
  Lock, 
  Server, 
  Cpu, 
  Database, 
  ChevronDown, 
  ChevronUp, 
  Layers
} from 'lucide-react';

interface TechLeadLandingPageProps {
  setActivePage?: (page: PageView) => void;
  onOpenLeadModal: () => void;
}

export const TechLeadLandingPage: React.FC<TechLeadLandingPageProps> = ({ onOpenLeadModal }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const scrollToSandbox = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById('forge-sandbox-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const faqs = [
    {
      q: "I don't want a cloud software platform reaching into my private network.",
      a: "MUSE Telemetry never reaches into your network. The collector sits behind your firewall and initiates all communication outbound over TLS 1.3. We never request or open inbound ports, and your devices are never exposed to the public internet."
    },
    {
      q: "Heavy SNMP walks spike CPU on our OLTs and CMTS boxes. How do you handle this?",
      a: "We actively avoid heavy SNMP walks on CPU-constrained control planes. MUSE Telemetry defaults to lightweight SSH/terminal scraping where it is lighter on device CPU, or uses targeted, single-OID SNMP requests where that is more efficient."
    },
    {
      q: "Is our network data stored in a multi-tenant cloud database alongside other ISPs?",
      a: "No. Every ISP customer receives an isolated, dedicated Google Cloud project and standalone data store. Your telemetry, topology maps, and credentials never touch a shared multi-tenant database."
    },
    {
      q: "What happens if our backhaul or internet connection drops?",
      a: "The collector caches telemetry locally using LUKS-encrypted on-premises storage. Once backhaul connectivity is restored, cached metrics flush to your cloud project automatically with zero data loss."
    },
    {
      q: "How does MUSE Forge handle new or custom device types?",
      a: "You paste sample terminal output into the Forge sandbox or schema editor. MUSE Forge parses fields and proposes metrics, thresholds, and alert rules. You inspect and approve the schema, and the collector begins polling immediately."
    }
  ];

  return (
    <div className="landing-page tech-lead-theme">
      {/* 1. HERO: FORGE-FIRST & AGNOSTIC */}
      <section className="landing-hero-section">
        <div className="section-container">
          <div className="hero-content-center">
            <h1 className="hero-title">
              Monitor the devices your NMS can't, without waiting for vendor drivers.
            </h1>

            <p className="hero-subtitle">
              Run a command, paste the terminal output into MUSE Forge, and get a proposed schema with metrics, states, thresholds, and alerts. You review and adjust, then it starts collecting. Minutes, not months.
            </p>

            <div className="dual-cta-wrapper">
              <div className="cta-block">
                <a href="#forge-sandbox-section" onClick={scrollToSandbox} className="btn btn-primary btn-hero-lg">
                  <Terminal size={16} />
                  <span>Launch the MUSE Forge Sandbox</span>
                </a>
                <p className="cta-microcopy">
                  Opens in your browser: test real terminal output with no account needed.
                </p>
              </div>

              <div className="cta-block">
                <button onClick={onOpenLeadModal} className="btn btn-secondary btn-hero-lg">
                  <span>Book Architecture Walkthrough</span>
                  <ArrowRight size={16} />
                </button>
                <p className="cta-microcopy">
                  20 minutes: deep dive into collector security, CPU overhead &amp; pollers.
                </p>
              </div>
            </div>

            <div className="credibility-microline mt-4">
              <ShieldCheck size={14} className="text-muted-icon" />
              <span>Built by engineers who have run network operations for 30+ years and lived the SNMP-walk-spikes-the-CMTS problem firsthand.</span>
            </div>
          </div>
        </div>
      </section>

      {/* 2. HOW FORGE WORKS (Interactive Pipeline & Demo) */}
      <section id="forge-sandbox-section" className="section-padding border-top">
        <div className="section-container">
          <div className="section-heading text-center">
            <h2>How MUSE Forge Onboards Unrecognized Hardware in Minutes</h2>
            <p>No waiting for vendor drivers or writing custom SNMP MIB parsers from scratch.</p>
          </div>

          <div className="forge-pipeline-grid">
            <div className="pipeline-step">
              <span className="step-tag font-mono">01</span>
              <h4>Select Device Profile</h4>
              <p>Choose the hardware category or define a new device type in the portal.</p>
            </div>

            <div className="pipeline-step">
              <span className="step-tag font-mono">02</span>
              <h4>Run Diagnostic Command</h4>
              <p>Execute your standard status command via SSH or CLI on the hardware.</p>
            </div>

            <div className="pipeline-step">
              <span className="step-tag font-mono">03</span>
              <h4>Paste Output into MUSE Forge</h4>
              <p>Drop raw terminal text directly into the parser window.</p>
            </div>

            <div className="pipeline-step">
              <span className="step-tag font-mono">04</span>
              <h4>Proposed Schema Generated</h4>
              <p>Forge extracts metrics (dBm, temp, drops), states, and alert thresholds.</p>
            </div>

            <div className="pipeline-step highlight-step">
              <span className="step-tag font-mono green-txt">05</span>
              <h4>Engineer Review &amp; Approval</h4>
              <p><strong>You review, adjust, and approve</strong> schema rules before anything goes live.</p>
            </div>

            <div className="pipeline-step">
              <span className="step-tag font-mono">06</span>
              <h4>Poller Updates Live</h4>
              <p>Save and assign the profile. The poller begins continuous collection immediately.</p>
            </div>
          </div>

          {/* INTERACTIVE FORGE SANDBOX COMPONENT */}
          <div className="mt-4">
            <MuseForgePlayground />
          </div>
        </div>
      </section>

      {/* 3. DEEP ARCHITECTURE & TRUST */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="section-heading text-center">
            <h2>Architecture Built for High-Availability Networks</h2>
            <p>Zero open inbound ports, CPU-conscious polling, and complete single-tenant isolation.</p>
          </div>

          {/* Interactive End-to-End Pipeline Visualizer */}
          <div className="mb-4">
            <PlatformArchitectureVisualizer />
          </div>

          <div className="grid-2">
            <div className="flat-card arch-card">
              <div className="card-header-dot">
                <Lock size={18} className="icon-blue" />
                <span className="card-tag font-mono">NETWORKING &amp; FIREWALL</span>
              </div>
              <h3>Outbound-Only Collector Appliance</h3>
              <p>
                Deploy a pre-configured hardware appliance or stand up a Docker container behind your firewall on a private IP. 
                The collector operates strictly <strong>outbound-only over TLS 1.3</strong>, requiring <strong>zero open inbound ports, no public IP, and zero NAT rule modifications</strong>.
              </p>
            </div>

            <div className="flat-card arch-card">
              <div className="card-header-dot">
                <Cpu size={18} className="icon-blue" />
                <span className="card-tag font-mono">HARDWARE OVERHEAD</span>
              </div>
              <h3>CPU-Conscious Polling (SSH vs. SNMP)</h3>
              <p>
                Heavy SNMP walks can spike CPU on OLTs, CMTS, and edge routers. MUSE Telemetry defaults to lightweight terminal/SSH scraping where it uses less device CPU, or targeted SNMP requests where that is more efficient. Protect your control plane while maintaining granular polling.
              </p>
            </div>

            <div className="flat-card arch-card">
              <div className="card-header-dot">
                <Database size={18} className="icon-blue" />
                <span className="card-tag font-mono">STORAGE &amp; RESILIENCY</span>
              </div>
              <h3>Store-and-Forward Caching &amp; Isolation</h3>
              <p>
                If backhaul drops, telemetry is cached locally on LUKS-encrypted disk storage and flushes automatically upon reconnect. Each ISP receives a <strong>100% single-tenant, isolated cloud project</strong> with zero co-mingled data buckets or shared databases.
              </p>
            </div>

            <div className="flat-card arch-card">
              <div className="card-header-dot">
                <ShieldCheck size={18} className="icon-blue" />
                <span className="card-tag font-mono">SECURITY &amp; ENCRYPTION</span>
              </div>
              <h3>Industry Security &amp; Encryption Standards</h3>
              <ul className="spec-list">
                <li><strong>In Transit:</strong> TLS 1.3 (TLS 1.2 minimum) with AES-256 encryption.</li>
                <li><strong>At Rest (Cloud):</strong> AES-256 encryption across all dedicated storage.</li>
                <li><strong>Appliance Disk:</strong> Full-disk encryption via LUKS / dm-crypt.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 4. CLUSTERING: CORRELATION NOT FORWARDING */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="flat-card correlation-card">
            <div className="card-header-dot">
              <Layers size={18} className="icon-blue" />
              <span className="card-tag font-mono">TELEMETRY PROCESSING</span>
            </div>
            <h2>Event Clustering: Correlation, Not Alert Forwarding</h2>
            <p className="lead-text">
              Most "unified" monitoring tools just forward raw alerts into a noisier inbox. MUSE Telemetry processes physical network topology in real time as telemetry is ingested.
            </p>

            <div className="grid-3 mt-3">
              <div className="cluster-subcard">
                <h4>1. Ingest &amp; Topology Correlation</h4>
                <p>Telemetry failures are correlated against geolocation, physical card/slot/port structures, splitters, and shared upstream dependencies.</p>
              </div>

              <div className="cluster-subcard">
                <h4>2. Noise Suppression</h4>
                <p>Simultaneous failures on shared hardware are grouped into ONE root-cause alert; dozens of downstream customer alarms are automatically suppressed.</p>
              </div>

              <div className="cluster-subcard">
                <h4>3. Clean Outbound Alerts</h4>
                <p>Correlated root-cause alerts flow out to PagerDuty, Opsgenie, webhooks, SMS, and email, paging engineers only when action is required.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. STRAIGHT ANSWERS (Q&A ACCORDION) */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="section-heading text-center">
            <h2>Straight Answers to Technical Objections</h2>
            <p>Unfiltered answers to the questions network engineers ask us most.</p>
          </div>

          <div className="faq-container">
            {faqs.map((faq, idx) => (
              <div key={idx} className={`faq-item ${openFaqIndex === idx ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleFaq(idx)}>
                  <span className="q-text">{faq.q}</span>
                  {openFaqIndex === idx ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {openFaqIndex === idx && (
                  <div className="faq-answer">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. FOUNDER CREDIBILITY & PROOF */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="flat-card founder-tech-card">
            <div className="founder-card-badge">
              <Server size={18} />
              <span>30+ YEARS IN THE TRENCHES</span>
            </div>
            <h2>Built by Engineers Who Managed Scale</h2>
            <p className="founder-body">
              MUSE Telemetry was built by network engineers who spent over 30 years managing high-availability infrastructure, scaling nationwide dial-up/DSL ingestion at America Online, expanding internet peering points, and building custom ISP monitoring from scratch.
            </p>
            <p className="founder-body mt-2">
              We offer a <strong>founder-led live network pilot on your production traffic</strong> with direct setup by the engineer who built the platform, alongside our interactive Forge sandbox which you can test right now in your browser.
            </p>
          </div>
        </div>
      </section>

      {/* 7. CLOSE: DUAL CTA */}
      <section className="section-padding border-top pb-6">
        <div className="section-container">
          <div className="flat-card cta-card-center tech-closing-card">
            <h2>Test Your Commands in the Sandbox or Walk the Architecture</h2>
            <p className="cta-lead">
              Try raw CLI parsing in your browser right now, or schedule a technical walkthrough with a senior infrastructure architect.
            </p>

            <div className="dual-cta-wrapper justify-center mt-3">
              <div className="cta-block">
                <a href="#forge-sandbox-section" onClick={scrollToSandbox} className="btn btn-primary btn-hero-lg">
                  <Terminal size={16} />
                  <span>Launch the MUSE Forge Sandbox</span>
                </a>
                <p className="cta-microcopy">
                  Opens in your browser: test real terminal output with no account needed.
                </p>
              </div>

              <div className="cta-block">
                <button onClick={onOpenLeadModal} className="btn btn-secondary btn-hero-lg">
                  <span>Book Architecture Walkthrough</span>
                  <ArrowRight size={16} />
                </button>
                <p className="cta-microcopy">
                  20 minutes: deep dive into collector security, CPU overhead &amp; pollers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STYLES FOR TECH LEAD LANDING PAGE */}
      <style>{`
        .tech-landing-page {
          width: 100%;
        }

        .landing-hero-section {
          padding: 5.5rem 0 3.5rem;
          text-align: center;
        }

        .hero-content-center {
          max-width: 880px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .hero-title {
          font-size: 2.85rem;
          font-weight: 800;
          color: #ffffff;
          line-height: 1.18;
          letter-spacing: -0.035em;
          margin-bottom: 1.25rem;
        }

        .hero-subtitle {
          font-size: 1.12rem;
          color: #999999;
          line-height: 1.6;
          margin-bottom: 2rem;
          max-width: 780px;
        }

        .dual-cta-wrapper {
          display: flex;
          align-items: flex-start;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .justify-center {
          justify-content: center;
        }

        .cta-block {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        .btn-hero-lg {
          padding: 0.85rem 1.65rem;
          font-size: 0.95rem;
          font-weight: 600;
          border-radius: var(--radius-sm);
        }

        .cta-microcopy {
          font-size: 0.78rem;
          color: var(--text-muted);
          max-width: 320px;
          line-height: 1.4;
          text-align: center;
        }

        .credibility-microline {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-size: 0.8rem;
          color: var(--text-dim);
          max-width: 720px;
          line-height: 1.4;
        }

        .text-muted-icon {
          color: var(--text-dim);
          flex-shrink: 0;
        }

        /* FORGE PIPELINE GRID */
        .forge-pipeline-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 0.85rem;
          margin-top: 2rem;
        }

        .pipeline-step {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          padding: 1.25rem 0.85rem;
          display: flex;
          flex-direction: column;
        }

        .pipeline-step.highlight-step {
          background: #040906;
          border-color: #10b981;
        }

        .step-tag {
          font-size: 0.75rem;
          font-weight: 700;
          color: var(--text-dim);
          margin-bottom: 0.5rem;
        }

        .green-txt { color: #10b981 !important; }

        .pipeline-step h4 {
          font-size: 0.85rem;
          margin-bottom: 0.35rem;
          line-height: 1.3;
        }

        .pipeline-step p {
          font-size: 0.75rem;
          line-height: 1.4;
          color: var(--text-muted);
        }

        /* ARCHITECTURE CARDS */
        .arch-card h3 {
          font-size: 1.15rem;
          margin-bottom: 0.65rem;
        }

        .arch-card p {
          font-size: 0.88rem;
          line-height: 1.55;
        }

        .spec-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          font-size: 0.85rem;
          color: var(--text-muted);
        }

        .spec-list strong {
          color: #ffffff;
        }

        .icon-blue { color: #3b82f6; }

        /* CORRELATION CARD */
        .correlation-card {
          background: #070707;
          border: 1px solid var(--border-subtle);
          padding: 2.75rem 2.25rem;
        }

        .correlation-card h2 {
          font-size: 1.85rem;
          margin-bottom: 0.5rem;
        }

        .cluster-subcard {
          background: #000000;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          padding: 1.25rem;
        }

        .cluster-subcard h4 {
          font-size: 0.95rem;
          color: #ffffff;
          margin-bottom: 0.4rem;
        }

        .cluster-subcard p {
          font-size: 0.82rem;
          line-height: 1.5;
        }

        /* FAQ ACCORDION */
        .faq-container {
          max-width: 860px;
          margin: 2rem auto 0;
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
        }

        .faq-item {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          overflow: hidden;
        }

        .faq-question {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.25rem 1.5rem;
          background: none;
          border: none;
          color: #ffffff;
          font-family: var(--font-body);
          font-size: 0.98rem;
          font-weight: 600;
          text-align: left;
          cursor: pointer;
          transition: background 0.15s ease;
        }

        .faq-question:hover {
          background: #111111;
        }

        .faq-answer {
          padding: 0 1.5rem 1.25rem;
          color: var(--text-muted);
          font-size: 0.9rem;
          line-height: 1.6;
          border-top: 1px dashed var(--border-color);
          padding-top: 1rem;
        }

        .faq-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
          margin-top: 0.5rem;
        }

        .faq-list strong {
          color: #ffffff;
        }

        /* FOUNDER CARD */
        .founder-tech-card {
          background: #080808;
          border: 1px solid var(--border-color);
          padding: 2.5rem;
        }

        .founder-card-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.72rem;
          font-weight: 700;
          color: var(--text-dim);
          letter-spacing: 0.05em;
          margin-bottom: 0.85rem;
        }

        .founder-tech-card h2 {
          font-size: 1.75rem;
          margin-bottom: 1rem;
        }

        .founder-body {
          font-size: 0.95rem;
          line-height: 1.65;
          color: var(--text-muted);
          max-width: 900px;
        }

        .tech-closing-card {
          padding: 3.5rem 2rem;
        }

        .mb-2 { margin-bottom: 0.5rem; }
        .mb-3 { margin-bottom: 0.75rem; }
        .mt-2 { margin-top: 0.5rem; }
        .mt-3 { margin-top: 0.75rem; }
        .mt-4 { margin-top: 1.5rem; }

        @media (max-width: 950px) {
          .forge-pipeline-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .hero-title { font-size: 2.1rem; }
          .dual-cta-wrapper { flex-direction: column; align-items: center; }
        }
      `}</style>
    </div>
  );
};

export default TechLeadLandingPage;
